// Custom styled dropdown for ApplicationStatus
// Replaces native <select> to allow full control over trigger (no border) and open dropdown appearance
// Follows same fixed-position pattern as CountrySelect to escape overflow:hidden parents
// Select-only combobox (no search): focus stays on the trigger, keys via useListboxNavigation.

import { useState, useRef, useEffect } from 'react';
import type { CSSProperties } from 'react';
import { ApplicationStatus } from '@startus/applications';
import { useTranslation } from 'react-i18next';
import { useListboxNavigation } from '@startus/ui';

const STATUSES = Object.values(ApplicationStatus);

interface StatusSelectProps {
  value: ApplicationStatus;
  onChange: (status: ApplicationStatus) => void;
  statusLabels: Record<ApplicationStatus, string>;
  statusColors: Record<ApplicationStatus, string>;
  className?: string;
}

export function StatusSelect({
  value,
  onChange,
  statusLabels,
  statusColors,
  className = '',
}: StatusSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Recompute fixed position on open
  useEffect(() => {
    if (!isOpen || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setDropdownStyle({ top: rect.bottom + 4, left: rect.left, minWidth: rect.width });
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!containerRef.current?.contains(t) && !dropdownRef.current?.contains(t)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const { t } = useTranslation('common');

  const handleSelect = (status: ApplicationStatus) => {
    onChange(status);
    setIsOpen(false);
  };

  const listbox = useListboxNavigation({
    items: STATUSES,
    isOpen,
    onSelect: handleSelect,
    onOpen: () => setIsOpen(true),
    onClose: () => setIsOpen(false),
    selectedIndex: STATUSES.indexOf(value),
    selectOnly: true,
  });

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger — flat, no border, text-only with chevron */}
      <button
        type="button"
        {...listbox.getComboboxProps()}
        // A combobox isn't named by its content; the visible text is announced as its value.
        aria-label={t('dropdown.applicationStatus')}
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex items-center gap-1 text-sm text-base-content/60 bg-transparent border-0 outline-none focus:outline-none shadow-none cursor-pointer py-0"
      >
        <span>{statusLabels[value]}</span>
        <svg
          className={`w-3.5 h-3.5 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 20 20"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 8l4 4 4-4" />
        </svg>
      </button>

      {/* Fixed-position dropdown — same pattern as CountrySelect */}
      {isOpen && (
        <div
          ref={dropdownRef}
          style={dropdownStyle}
          className="fixed z-9999 bg-base-100 border border-base-300 rounded-lg shadow-lg overflow-hidden"
        >
          <ul {...listbox.getListboxProps()}>
            {STATUSES.map((s, index) => (
              <li
                key={s}
                {...listbox.getOptionProps(index, value === s)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(s)}
                className={`flex items-center gap-2 px-4 py-2 text-sm cursor-pointer hover:bg-base-200 ${listbox.isActive(index) ? 'bg-base-200' : ''} ${value === s ? 'bg-primary/10 font-medium' : ''}`}
              >
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: statusColors[s] }}
                />
                <span>{statusLabels[s]}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
